import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Heart, MapPin, Calendar, Target, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const supportOptions = [
  { id: 'donate_turkeys', label: 'Donate whole turkeys' },
  { id: 'monetary', label: 'Make a monetary contribution' },
  { id: 'sponsorship', label: 'Provide a business sponsorship' },
  { id: 'organize_drive', label: 'Organize a turkey or monetary donation drive' },
  { id: 'share_event', label: 'Share the event with employees, customers, or community members' },
  { id: 'other_resources', label: 'Provide other resources or assistance' },
  { id: 'future_partnership', label: 'Discuss a future partnership with the W.H.Y.M.E. Foundation' },
];

const monetaryOptions = ['$25', '$50', '$100', '$250', '$500', 'Other'];

const TurkeyDrive = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: '', contact_person: '', title: '', phone: '', email: '',
    website: '', support_methods: [], turkey_count: '', monetary_amount: '',
    other_amount: '', recognition: '', comments: ''
  });

  const toggleSupport = (id) => {
    setForm(f => ({
      ...f,
      support_methods: f.support_methods.includes(id)
        ? f.support_methods.filter(s => s !== id)
        : [...f.support_methods, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_person || !form.phone || !form.email || !form.recognition || form.support_methods.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const body = { ...form };
      if (body.monetary_amount === 'Other') {
        body.monetary_amount = `Other: $${body.other_amount}`;
      }
      const res = await fetch(`${API}/api/turkey-drive/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Submission failed'); return; }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error('Network error'); } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-[#E6B933]/5 to-white">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="turkey-drive-success">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Thank You!</h1>
          <p className="text-lg text-gray-600 mb-2">Your sponsorship interest has been submitted.</p>
          <p className="text-gray-500">The W.H.Y.M.E. Foundation will be in touch shortly. Together, we can turn generosity into impact.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="turkey-drive-page">
      {/* Header */}
      <section className="bg-black py-16 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-[#E6B933] font-semibold text-sm tracking-widest uppercase mb-4">The W.H.Y.M.E. Foundation</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3" data-testid="turkey-drive-title">
            2026 Thanksgiving <span className="text-[#E6B933]">Turkey Drive</span>
          </h1>
          <p className="text-lg text-gray-300 mb-6">Community Sponsorship & Donation Request</p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-[#E6B933]"><Target className="w-4 h-4" /> <span className="text-white">Goal: <strong className="text-[#E6B933]">100 Turkeys</strong> or <strong className="text-[#E6B933]">$2,500</strong></span></div>
            <div className="flex items-center gap-2 text-[#E6B933]"><Calendar className="w-4 h-4" /> <span className="text-white">Deadline: <strong className="text-[#E6B933]">November 18, 2026</strong></span></div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Letter */}
        <Card className="p-8 md:p-10 mb-10 bg-white" data-testid="turkey-drive-letter">
          <p className="text-gray-700 mb-4 font-medium">Dear Community Partner,</p>
          <p className="text-gray-600 mb-4 leading-relaxed">
            The W.H.Y.M.E. Foundation invites you to join us in making Thanksgiving a little brighter for families in our community through our <strong>2026 Thanksgiving Turkey Drive</strong>.
          </p>
          <p className="text-gray-600 mb-4 leading-relaxed">
            This year, our goal is to collect <strong>100 turkeys</strong> or raise <strong>$2,500</strong> in monetary contributions to help provide Thanksgiving support to families who may need a helping hand during the holiday season.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            We believe that when businesses, organizations, and individuals come together with a shared purpose, we can make a meaningful difference. Every turkey donated and every dollar contributed brings us one step closer to making this Thanksgiving special for families in our community.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mb-4">Ways to Support Our Mission</h3>
          <ul className="space-y-2 text-gray-600 mb-6">
            <li className="flex items-start gap-2"><Heart className="w-4 h-4 text-[#E6B933] mt-1 flex-shrink-0" /><span><strong>Turkey Donations:</strong> Help us reach our goal of 100 turkeys.</span></li>
            <li className="flex items-start gap-2"><Heart className="w-4 h-4 text-[#E6B933] mt-1 flex-shrink-0" /><span><strong>Monetary Contributions:</strong> Help us reach our fundraising goal of $2,500.</span></li>
            <li className="flex items-start gap-2"><Heart className="w-4 h-4 text-[#E6B933] mt-1 flex-shrink-0" /><span><strong>Business Sponsorships:</strong> Partner with us to support the Thanksgiving Turkey Drive.</span></li>
            <li className="flex items-start gap-2"><Heart className="w-4 h-4 text-[#E6B933] mt-1 flex-shrink-0" /><span><strong>Community Partnerships:</strong> Encourage your employees, members, customers, and networks to participate.</span></li>
            <li className="flex items-start gap-2"><Heart className="w-4 h-4 text-[#E6B933] mt-1 flex-shrink-0" /><span><strong>Additional Donations:</strong> Contribute other approved resources that can help support the drive.</span></li>
          </ul>

          <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-3">Event Information</h4>
            <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-600">
              <div><strong>Event:</strong> W.H.Y.M.E. Foundation Thanksgiving Turkey Drive</div>
              <div><strong>Turkey Donation Deadline:</strong> November 18, 2026</div>
              <div><strong>Turkey Collection Goal:</strong> 100 turkeys</div>
              <div><strong>Monetary Fundraising Goal:</strong> $2,500</div>
              <div className="sm:col-span-2 flex items-start gap-2 mt-1">
                <MapPin className="w-4 h-4 text-[#E6B933] mt-0.5 flex-shrink-0" />
                <span><strong>Drop-Off:</strong> Missing Element Sports Performance & Fitness, 7315 Five Forks Drive, Spring, TX 77379</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Sponsorship Form */}
        <Card className="p-8 md:p-10 border-t-4 border-[#E6B933]" data-testid="turkey-drive-form">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Sponsorship & Donation Interest Form</h2>
          <p className="text-gray-500 text-sm mb-8">Fields marked with * are required</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700">Business or Organization Name *</Label>
                <Input value={form.business_name} onChange={e => setForm(f => ({...f, business_name: e.target.value}))} placeholder="Your business name" className="mt-1" required data-testid="td-business-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Contact Person *</Label>
                <Input value={form.contact_person} onChange={e => setForm(f => ({...f, contact_person: e.target.value}))} placeholder="Full name" className="mt-1" required data-testid="td-contact-person" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Title</Label>
                <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Your title" className="mt-1" data-testid="td-title" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Phone Number *</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="(555) 123-4567" className="mt-1" required data-testid="td-phone" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Email Address *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="you@business.com" className="mt-1" required data-testid="td-email" />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Website or Social Media Page</Label>
                <Input value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} placeholder="https://..." className="mt-1" data-testid="td-website" />
              </div>
            </div>

            {/* Support Methods */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-3 block">How Would You Like to Support the Turkey Drive? * <span className="text-gray-400 font-normal">(Select all that apply)</span></Label>
              <div className="space-y-2">
                {supportOptions.map(opt => (
                  <label key={opt.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                    form.support_methods.includes(opt.id) ? 'border-[#E6B933] bg-[#E6B933]/5' : 'border-gray-200 hover:border-gray-300'
                  }`} data-testid={`td-support-${opt.id}`}>
                    <input
                      type="checkbox"
                      checked={form.support_methods.includes(opt.id)}
                      onChange={() => toggleSupport(opt.id)}
                      className="w-4 h-4 rounded border-gray-300 text-[#E6B933] focus:ring-[#E6B933]"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Turkey Count */}
            <div>
              <Label className="text-sm font-medium text-gray-700">Number of turkeys you anticipate donating, if applicable</Label>
              <Input value={form.turkey_count} onChange={e => setForm(f => ({...f, turkey_count: e.target.value}))} placeholder="e.g. 5" className="mt-1 max-w-xs" data-testid="td-turkey-count" />
            </div>

            {/* Monetary Amount */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-3 block">Monetary contribution amount, if applicable</Label>
              <div className="flex flex-wrap gap-2">
                {monetaryOptions.map(amt => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setForm(f => ({...f, monetary_amount: amt}))}
                    className={`px-5 py-2.5 rounded-lg border text-sm font-medium transition-all duration-200 ${
                      form.monetary_amount === amt
                        ? 'border-[#E6B933] bg-[#E6B933] text-black'
                        : 'border-gray-200 text-gray-700 hover:border-[#E6B933]/50'
                    }`}
                    data-testid={`td-amount-${amt.replace('$', '')}`}
                  >
                    {amt}
                  </button>
                ))}
              </div>
              {form.monetary_amount === 'Other' && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-gray-500 font-medium">$</span>
                  <Input value={form.other_amount} onChange={e => setForm(f => ({...f, other_amount: e.target.value}))} placeholder="Enter amount" className="max-w-xs" data-testid="td-other-amount" />
                </div>
              )}
            </div>

            {/* Recognition */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-3 block">Would your business or organization like to be recognized as a community supporter? *</Label>
              <div className="space-y-2">
                {[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'contact_me', label: 'Please contact me to discuss recognition options' },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                    form.recognition === opt.value ? 'border-[#E6B933] bg-[#E6B933]/5' : 'border-gray-200 hover:border-gray-300'
                  }`} data-testid={`td-recognition-${opt.value}`}>
                    <input
                      type="radio"
                      name="recognition"
                      checked={form.recognition === opt.value}
                      onChange={() => setForm(f => ({...f, recognition: opt.value}))}
                      className="w-4 h-4 border-gray-300 text-[#E6B933] focus:ring-[#E6B933]"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Comments */}
            <div>
              <Label className="text-sm font-medium text-gray-700">Additional Comments or Questions</Label>
              <textarea
                value={form.comments}
                onChange={e => setForm(f => ({...f, comments: e.target.value}))}
                placeholder="Anything else you'd like us to know..."
                rows={4}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#E6B933] focus:ring-[#E6B933]/20 focus:outline-none"
                data-testid="td-comments"
              />
            </div>

            {/* Submit */}
            <Button type="submit" disabled={loading} className="w-full bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold h-12 text-base transition-all duration-300" data-testid="td-submit-btn">
              {loading ? 'Submitting...' : 'Submit Sponsorship Interest'}
            </Button>

            <p className="text-center text-xs text-gray-400 mt-4">
              Together, we can turn generosity into impact.
            </p>
          </form>
        </Card>

        {/* Closing */}
        <div className="text-center mt-12 mb-8">
          <p className="text-gray-600 italic">With gratitude,</p>
          <p className="text-gray-900 font-bold text-lg">The W.H.Y.M.E. Foundation</p>
        </div>
      </div>
    </div>
  );
};

export default TurkeyDrive;
